import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Chrome, Mail, Lock, User, Loader2, HelpCircle, CheckCircle } from 'lucide-react';
import LatteIcon from '../../components/common/LatteIcon';
import LatteButton from '../../components/common/LatteButton';
import LatteSpinner from '../../components/common/LatteSpinner';
import { SEOHelmet } from '../../components/common';


const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signInWithGoogle, signInWithEmail, signUpWithEmail, loading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // location state에서 메시지 확인
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // state 초기화 (뒤로가기 후 다시 오면 안보이게)
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // 이미 로그인된 경우 메인 페이지로 리다이렉트
  if (user && !loading) {
    return <Navigate to="/" replace />;
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const result = await signInWithGoogle();
      if (!result.success) {
        setError(result.error || '로그인에 실패했습니다.');
      }
      // 성공 시 자동으로 리다이렉트됨
    } catch (error) {
      setError('로그인 중 오류가 발생했습니다.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await signInWithEmail(email, password);
      if (!result.success) {
        setError(result.error || '이메일 로그인에 실패했습니다.');
      }
    } catch (error) {
      setError('로그인 중 오류가 발생했습니다.');
      console.error('Email login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await signUpWithEmail(email, password, name);
      if (!result.success) {
        setError(result.error || '회원가입에 실패했습니다.');
      } else {
        setError('');
        alert('회원가입이 완료되었습니다! 이메일을 확인해주세요.');
        setShowSignUp(false);
        setShowEmailLogin(true);
      }
    } catch (error) {
      setError('회원가입 중 오류가 발생했습니다.');
      console.error('Sign up error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container flex-center">
        <div className="flex flex-col items-center">
          <LatteSpinner size="md" className="mb-4" />
          <p className="text-gray-600">로그인 상태 확인 중...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHelmet 
        title="로그인"
        description="라떼이즈(Latteis)에 로그인하여 AI 이력서 컨설팅 서비스를 이용해보세요."
        keywords="로그인, 회원가입, AI 이력서"
        url="/login"
        type="website"
      />
      <div className="page-container bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex-center p-4">
      <div className="max-w-md w-full">
        {/* 헤더 */}
        <div className="text-center mb-8">
          <div className="flex-center mb-4">
            <LatteIcon size="xl" variant="background" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Latte is</h1>
          <p className="text-gray-600">AI와 함께 만드는 나만의 커리어 스토리</p>
        </div>

        {/* 로그인 카드 */}
        <div className="card card-lg">
          {/* 성공 메시지 */}
          {successMessage && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6 flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}
          
          {/* 에러 메시지 */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Google 로그인 */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white border border-gray-300 rounded-lg transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Chrome className="w-5 h-5" />
            )}
            <span className="font-medium text-gray-700">
              Google로 시작하기
            </span>
          </button>

          {/* 구분선 */}
          <div className="divider-with-text">
            <div className="divider-line"></div>
            <span className="divider-text">또는</span>
            <div className="divider-line"></div>
          </div>

          {/* 이메일 로그인 토글 */}
          {!showEmailLogin && !showSignUp ? (
            <>
              <button
                onClick={() => setShowEmailLogin(true)}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-gray-100 rounded-lg transition-colors duration-150 hover:bg-gray-200 mb-2"
              >
                <Mail className="w-5 h-5 text-gray-600" />
                <span className="font-medium text-gray-700">이메일로 로그인</span>
              </button>
              <button
                onClick={() => setShowSignUp(true)}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-latte-lightest rounded-lg transition-colors duration-150 hover:bg-latte-light"
              >
                <User className="w-5 h-5 text-latte" />
                <span className="font-medium text-latte-dark">이메일로 회원가입</span>
              </button>
            </>
          ) : showSignUp ? (
            /* 회원가입 폼 */
            <form onSubmit={handleEmailSignUp}>
              <div className="form-group">
                <label className="label">
                  이름
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input pl-10"
                    placeholder="이름을 입력하세요"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="label">
                  이메일
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                    placeholder="your@example.com"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="label">
                  비밀번호
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              <LatteButton
                type="submit"
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <LatteSpinner size="sm" />
                ) : (
                  '회원가입'
                )}
              </LatteButton>
              <div className="flex gap-2 text-sm mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSignUp(false);
                    setShowEmailLogin(true);
                  }}
                  className="text-latte-primary hover:text-latte-dark transition-colors"
                >
                  이미 계정이 있으신가요?
                </button>
                <span className="text-gray-400">|</span>
                <button
                  type="button"
                  onClick={() => {
                    setShowSignUp(false);
                    setShowEmailLogin(false);
                  }}
                  className="text-gray-600 hover:text-gray-700 transition-colors"
                >
                  뒤로 가기
                </button>
              </div>
            </form>
          ) : (
            /* 이메일 로그인 폼 */
            <form onSubmit={handleEmailLogin}>
              <div className="form-group">
                <label className="label">
                  이메일
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input pl-10"
                    placeholder="your@example.com"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="label">
                  비밀번호
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              <LatteButton
                type="submit"
                disabled={isLoading}
                className="w-full"
              >
                {isLoading ? (
                  <LatteSpinner size="sm" />
                ) : (
                  '로그인'
                )}
              </LatteButton>
              <div className="flex gap-2 text-sm mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailLogin(false);
                    setShowSignUp(true);
                  }}
                  className="text-latte-primary hover:text-latte-dark transition-colors"
                >
                  계정이 없으신가요?
                </button>
                <span className="text-gray-400">|</span>
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailLogin(false);
                    setShowSignUp(false);
                  }}
                  className="text-gray-600 hover:text-gray-700 transition-colors"
                >
                  뒤로 가기
                </button>
              </div>
            </form>
          )}

          {/* 안내 문구 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              계정이 없으시면 Google 로그인 시 자동으로 계정이 생성됩니다.
            </p>
          </div>
        </div>

        {/* 가이드 링크 */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/guide')}
            className="inline-flex items-center gap-2 px-4 py-2 text-latte-primary rounded-lg transition-all duration-150 hover:text-latte-dark hover:bg-latte-lightest"
          >
            <HelpCircle className="w-4 h-4" />
            사용자 가이드 보기
          </button>
        </div>

        {/* 푸터 */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Latte is v1.0 프로토타입 • AI 이력서 컨설팅 서비스
          </p>
        </div>
      </div>
      </div>
    </>
  );
};

export default LoginPage;